import React from 'react';
import { Link } from 'react-router-dom';
import { ShieldCheck, Sparkles, UserRound, UserRoundSearch } from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import {
  getIdentityDisplayName,
  getIdentitySubtitle,
  type IdentityStatus,
} from '../../lib/visitorIdentity';

type LeadIdentity = {
  first_name?: string | null;
  last_name?: string | null;
  email?: string | null;
  phone?: string | null;
  company?: string | null;
  job_title?: string | null;
};

type PersonIdentity = {
  full_name?: string | null;
  first_name?: string | null;
  last_name?: string | null;
  email?: string | null;
  title?: string | null;
};

type CompanyIdentity = {
  name?: string | null;
};

interface VisitorIdentitySummaryProps {
  status: IdentityStatus;
  lead?: LeadIdentity | null;
  person?: PersonIdentity | Record<string, unknown> | null;
  company?: CompanyIdentity | Record<string, unknown> | null;
  contactHref?: string;
  subtitle?: string | null;
  className?: string;
  /** When set, renders an identity-verified shield inline after the name
   * (tooltip = this string). Kept inside the name row so it sits next to the
   * name rather than floating beside the two-line block. */
  verifiedTitle?: string | null;
}

const enrichmentExplanation = 'This visitor has not self-identified. The displayed person or company comes from enrichment data and may be incorrect.';

const VisitorIdentitySummary: React.FC<VisitorIdentitySummaryProps> = ({
  status,
  lead,
  person,
  company,
  contactHref,
  subtitle,
  className,
  verifiedTitle,
}) => {
  const verifiedBadge = verifiedTitle ? (
    <span title={verifiedTitle} className="flex-shrink-0 text-emerald-500">
      <ShieldCheck className="h-3.5 w-3.5" />
    </span>
  ) : null;
  const typedPerson = person as PersonIdentity | null | undefined;
  const typedCompany = company as CompanyIdentity | null | undefined;
  const displayName = getIdentityDisplayName({ lead, person: typedPerson, company: typedCompany })
    || (status === 'contact' ? lead?.phone : null);
  const inferredSubtitle = subtitle ?? getIdentitySubtitle({ lead, person: typedPerson, company: typedCompany });
  const hasPersonMatch = Boolean(
    typedPerson?.full_name
    || typedPerson?.first_name
    || typedPerson?.last_name
    || typedPerson?.email,
  );

  if (status === 'identified' && displayName) {
    const matchSubtitle = hasPersonMatch ? inferredSubtitle : null;
    const provenanceLabel = hasPersonMatch ? 'Enriched profile' : 'Enriched company';

    return (
      <div className={cn('flex min-w-0 items-start gap-2', className)}>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <span
                className="mt-0.5 shrink-0 cursor-help text-violet-500"
                aria-label="Enrichment-derived identity"
                title={enrichmentExplanation}
              >
                <Sparkles className="h-3.5 w-3.5" />
              </span>
            </TooltipTrigger>
            <TooltipContent className="max-w-72">
              {enrichmentExplanation}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
        <div className="min-w-0">
          <div className="truncate text-sm font-medium">{displayName}</div>
          <div className="flex min-w-0 items-center gap-1 text-xs text-muted-foreground">
            {matchSubtitle && (
              <>
                <span className="truncate">{matchSubtitle}</span>
                <span aria-hidden="true">·</span>
              </>
            )}
            <span className="shrink-0">{provenanceLabel}</span>
          </div>
        </div>
      </div>
    );
  }

  if (status === 'contact' && displayName) {
    const name = contactHref ? (
      <Link
        to={contactHref}
        className="truncate text-sm font-medium hover:underline"
        onClick={(event) => event.stopPropagation()}
      >
        {displayName}
      </Link>
    ) : (
      <span className="truncate text-sm font-medium">{displayName}</span>
    );

    return (
      <div className={cn('flex min-w-0 items-start gap-2', className)}>
        <UserRound className="mt-0.5 h-3.5 w-3.5 shrink-0 text-blue-500" />
        <div className="min-w-0">
          <div className="flex min-w-0 items-center gap-1.5">
            {name}
            {verifiedBadge}
          </div>
          {inferredSubtitle && (
            <div className="truncate text-xs text-muted-foreground">{inferredSubtitle}</div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={cn('flex min-w-0 items-center gap-2', className)}>
      <UserRoundSearch className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
      <div className="truncate text-sm font-medium text-muted-foreground">Anonymous visitor</div>
    </div>
  );
};

export default VisitorIdentitySummary;
